/* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- form data and React node fallbacks rely on `||` empty-string/zero coercion to falsy fallback */
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon,
  PlusIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { DateInput } from '@/components/ui/DateInput';
import { toDateValue } from '@/utils/datetime';
import toast from 'react-hot-toast';
import {
  vendorService,
  Contract,
  ContractAmendment,
  ContractObligation,
  VendorDocument,
  AuditLogEntry,
} from '../services/vendor.service';

type TabKey = 'overview' | 'amendments' | 'obligations' | 'documents' | 'history';

const STATUS_COLORS: Record<string, string> = {
  success: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
  warning: 'bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
  danger: 'bg-danger-100 text-danger-800 border-danger-200 dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800',
  secondary: 'bg-secondary-100 text-secondary-800 border-secondary-200 dark:bg-secondary-800 dark:text-secondary-300 dark:border-secondary-700',
};

const AMENDMENT_STATUS_COLORS: Record<string, string> = {
  DRAFT: 'secondary',
  PENDING: 'warning',
  APPROVED: 'success',
  EXECUTED: 'success',
  REJECTED: 'danger',
  VOIDED: 'danger',
};

const FREQUENCY_LABELS: Record<string, string> = {
  OBL_MONTHLY: 'Monthly',
  OBL_QUARTERLY: 'Quarterly',
  OBL_SEMI_ANNUAL: 'Semi-Annual',
  OBL_ANNUAL: 'Annual',
};

const OBLIGATION_STATUS_COLORS: Record<string, string> = {
  PENDING: 'warning',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  WAIVED: 'secondary',
  OVERDUE: 'danger',
  BREACHED: 'danger',
};

const DOC_CATEGORY_COLORS: Record<string, string> = {
  CONTRACT: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
  AMENDMENT: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  CERTIFICATE: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  INSURANCE: 'bg-info-100 text-info-800 dark:bg-info-900/20 dark:text-info-300',
  REPORT: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
  OTHER: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
};

const CONTRACT_STATUS_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['UNDER_REVIEW'],
  UNDER_REVIEW: ['PENDING_APPROVAL', 'DRAFT'],
  PENDING_APPROVAL: ['ACTIVE', 'UNDER_REVIEW'],
  ACTIVE: ['EXPIRING_SOON', 'TERMINATED'],
  EXPIRING_SOON: ['EXPIRED', 'RENEWED'],
  EXPIRED: ['ARCHIVED'],
  RENEWED: ['ARCHIVED'],
  TERMINATED: ['ARCHIVED'],
  ARCHIVED: [],
};

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  UNDER_REVIEW: 'Under Review',
  PENDING_APPROVAL: 'Pending Approval',
  ACTIVE: 'Active',
  EXPIRING_SOON: 'Expiring Soon',
  EXPIRED: 'Expired',
  RENEWED: 'Renewed',
  TERMINATED: 'Terminated',
  ARCHIVED: 'Archived',
};

function getStatusBadge(color: string): string {
  return STATUS_COLORS[color] ?? STATUS_COLORS.secondary;
}

export default function ContractDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  // Tab-specific data
  const [amendments, setAmendments] = useState<ContractAmendment[]>([]);
  const [amendmentsLoading, setAmendmentsLoading] = useState(false);
  const [obligations, setObligations] = useState<ContractObligation[]>([]);
  const [obligationsLoading, setObligationsLoading] = useState(false);
  const [documents, setDocuments] = useState<VendorDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const fetchedTabs = useRef<Set<string>>(new Set());

  // Modal states
  const [showAmendmentModal, setShowAmendmentModal] = useState(false);
  const [showObligationModal, setShowObligationModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Edit/delete obligation state
  const [editingObligation, setEditingObligation] = useState<ContractObligation | null>(null);
  const [deletingObligationId, setDeletingObligationId] = useState<string | null>(null);

  // Edit/delete amendment state
  const [editingAmendment, setEditingAmendment] = useState<ContractAmendment | null>(null);
  const [deletingAmendmentId, setDeletingAmendmentId] = useState<string | null>(null);

  // Edit contract modal state
  const [showEditContractModal, setShowEditContractModal] = useState(false);
  const [editContractStatus, setEditContractStatus] = useState('');

  // Document drawer/edit/delete state
  const [previewDocument, setPreviewDocument] = useState<VendorDocument | null>(null);
  const [editingDocument, setEditingDocument] = useState<VendorDocument | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  const [documentVersions, setDocumentVersions] = useState<VendorDocument[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [versionUploading, setVersionUploading] = useState(false);
  const [versionProgress, setVersionProgress] = useState(0);
  const versionFileInputRef = useRef<HTMLInputElement>(null);

  const inputCls = 'w-full rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500';
  const labelCls = 'block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1';

  // Fetch contract
  useEffect(() => {
    const fetchContract = async () => {
      if (!id) {return;}
      try {
        setLoading(true);
        setError(null);
        const data = await vendorService.getContract(id);
        setContract(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load contract');
      } finally {
        setLoading(false);
      }
    };
    fetchContract();
  }, [id]);

  // Fetch tab data lazily (only once per tab)
  useEffect(() => {
    if (!id || fetchedTabs.current.has(activeTab)) {return;}

    if (activeTab === 'amendments') {
      fetchedTabs.current.add('amendments');
      setAmendmentsLoading(true);
      vendorService.getAmendments(id)
        .then(setAmendments)
        .catch(() => {/* handled by empty state */})
        .finally(() => setAmendmentsLoading(false));
    }

    if (activeTab === 'obligations') {
      fetchedTabs.current.add('obligations');
      setObligationsLoading(true);
      vendorService.getObligations(id)
        .then(setObligations)
        .catch(() => {/* handled by empty state */})
        .finally(() => setObligationsLoading(false));
    }

    if (activeTab === 'documents') {
      fetchedTabs.current.add('documents');
      setDocumentsLoading(true);
      vendorService.getContractDocuments(id)
        .then(setDocuments)
        .catch(() => {/* handled by empty state */})
        .finally(() => setDocumentsLoading(false));
    }

    if (activeTab === 'history') {
      fetchedTabs.current.add('history');
      setAuditLogLoading(true);
      vendorService.getContractAuditLog(id)
        .then((result) => setAuditLog(result.entries))
        .catch(() => {/* handled by empty state */})
        .finally(() => setAuditLogLoading(false));
    }
  }, [activeTab, id]);

  // Load document versions when preview drawer opens
  useEffect(() => {
    if (!previewDocument) {
      setDocumentVersions([]);
      return;
    }
    setVersionsLoading(true);
    vendorService.getDocumentVersions(previewDocument.id)
      .then(setDocumentVersions)
      .catch(() => setDocumentVersions([]))
      .finally(() => setVersionsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- previewDocument.id is the only relevant change
  }, [previewDocument?.id]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, duration: 0.3 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
  };

  const tabs: { key: TabKey; label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }[] = [
    { key: 'overview', label: 'Overview', icon: InformationCircleIcon },
    { key: 'amendments', label: 'Amendments', icon: DocumentDuplicateIcon },
    { key: 'obligations', label: 'Obligations', icon: ClipboardDocumentListIcon },
    { key: 'documents', label: 'Documents', icon: DocumentTextIcon },
    { key: 'history', label: 'History', icon: ClockIcon },
  ];

  // --- Loading / Error states ---

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-6">
        <Card>
          <CardContent className="p-12 text-center">
            <div className="text-lg text-secondary-600 dark:text-secondary-400">
              Loading contract details...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error ?? !contract) {
    return (
      <div className="p-4 sm:p-6 space-y-6">
        <Card>
          <CardContent className="p-12 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 dark:text-danger-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              Error Loading Contract
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400 mb-4">
              {error ?? 'Contract not found'}
            </p>
            <Button onClick={() => navigate('/legal/contracts')} variant="outline">
              Back to Contracts
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = vendorService.getContractStatusConfig(contract.status);

  // --- Helper render functions ---

  function renderField(label: string, value: React.ReactNode) {
    return (
      <div>
        <dt className="text-sm font-medium text-secondary-500 dark:text-secondary-400">{label}</dt>
        <dd className="mt-1 text-sm text-secondary-900 dark:text-white">{value || '-'}</dd>
      </div>
    );
  }

  function isOverdue(dueDate: string | undefined, status: string): boolean {
    if (!dueDate) {return false;}
    if (['COMPLETED', 'WAIVED'].includes(status)) {return false;}
    return new Date(dueDate) < new Date();
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) {return `${bytes} B`;}
    if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // --- Tab content renderers ---

  function renderOverviewTab() {
    if (!contract) {return null;}
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Contract Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {renderField('Contract Number', contract.contractNumber)}
              {renderField('Title', contract.title)}
              {renderField('Type', vendorService.getContractTypeLabel(contract.contractType))}
              {renderField('Status', (
                <span className={cn('px-2.5 py-0.5 text-xs font-medium rounded-full border', getStatusBadge(statusConfig.color))}>
                  {statusConfig.label}
                </span>
              ))}
              {renderField('Vendor', contract.vendor ? (
                <Link
                  to={`/legal/vendors/${contract.vendor.id}`}
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {contract.vendor.companyLegalName}
                </Link>
              ) : '-')}
              {renderField('Governing Law', contract.governingLaw)}
              {renderField('Confidentiality Level', (
                <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300">
                  {contract.confidentialityLevel}
                </span>
              ))}
            </dl>
          </CardContent>
        </Card>

        {/* Financial */}
        <Card>
          <CardHeader>
            <CardTitle>Financial Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {renderField('Contract Value', vendorService.formatCurrency(contract.contractValue, contract.currency))}
              {renderField('Annual Value', vendorService.formatCurrency(contract.annualValue, contract.currency))}
              {renderField('Currency', contract.currency)}
              {renderField('Payment Terms', contract.paymentTerms)}
              {renderField('Payment Frequency', contract.paymentFrequency?.replace(/_/g, ' '))}
              {renderField('Budget Code', contract.budgetCode)}
            </dl>
          </CardContent>
        </Card>

        {/* Dates */}
        <Card>
          <CardHeader>
            <CardTitle>Key Dates</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {renderField('Effective Date', vendorService.formatDate(contract.effectiveDate))}
              {renderField('Expiration Date', vendorService.formatDate(contract.expirationDate))}
              {renderField('Execution Date', vendorService.formatDate(contract.executionDate))}
              {renderField('Notice Period Days', contract.noticePeriodDays !== null && contract.noticePeriodDays !== undefined ? `${contract.noticePeriodDays} days` : '-')}
              {renderField('Notice Deadline', vendorService.formatDate(contract.noticeDeadline))}
              {renderField('Next Review Date', vendorService.formatDate(contract.nextReviewDate))}
            </dl>
          </CardContent>
        </Card>

        {/* Renewal */}
        <Card>
          <CardHeader>
            <CardTitle>Renewal Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {renderField('Auto-Renewal', contract.isAutoRenewal ? (
                <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300">Yes</span>
              ) : (
                <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300">No</span>
              ))}
              {renderField('Renewal Term', contract.autoRenewalTermMonths !== null && contract.autoRenewalTermMonths !== undefined ? `${contract.autoRenewalTermMonths} months` : '-')}
              {renderField('Max Renewals', contract.maxRenewalCount !== null && contract.maxRenewalCount !== undefined ? contract.maxRenewalCount.toString() : '-')}
              {renderField('Renewals to Date', contract.renewalCountToDate.toString())}
            </dl>
          </CardContent>
        </Card>

        {/* Description & Notes */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Description & Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-4">
              {renderField('Description', contract.description ? (
                <p className="whitespace-pre-wrap">{contract.description}</p>
              ) : '-')}
              {renderField('Internal Notes', contract.internalNotes ? (
                <p className="whitespace-pre-wrap">{contract.internalNotes}</p>
              ) : '-')}
              {renderField('Tags', contract.tags && contract.tags.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {contract.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : '-')}
            </dl>
          </CardContent>
        </Card>

        {/* Related Contracts */}
        {(contract.parentContract || (contract.childContracts && contract.childContracts.length > 0)) && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Related Contracts</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-4">
                {contract.parentContract && renderField('Parent Contract', (
                  <Link
                    to={`/legal/contracts/${contract.parentContract.id}`}
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {contract.parentContract.contractNumber} - {contract.parentContract.title}
                  </Link>
                ))}
                {contract.childContracts && contract.childContracts.length > 0 && renderField('Child Contracts', (
                  <ul className="space-y-1 mt-1">
                    {contract.childContracts.map((child) => {
                      const childStatus = vendorService.getContractStatusConfig(child.status);
                      return (
                        <li key={child.id} className="flex items-center gap-2">
                          <Link
                            to={`/legal/contracts/${child.id}`}
                            className="text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            {child.contractNumber} - {child.title}
                          </Link>
                          <span className="text-xs text-secondary-500 dark:text-secondary-400">
                            ({vendorService.getContractTypeLabel(child.contractType)})
                          </span>
                          <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full border', getStatusBadge(childStatus.color))}>
                            {childStatus.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ))}
              </dl>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  function renderAmendmentsTab() {
    if (!contract) {return null;}

    const handleAddAmendment = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const form = e.currentTarget;
      const data = new FormData(form);
      try {
        setModalSubmitting(true);
        const payload: Record<string, unknown> = {
          title: (data.get('title') as string).trim(),
          effectiveDate: data.get('effectiveDate') || undefined,
          description: (data.get('description') as string)?.trim() || undefined,
          valueImpact: data.get('valueImpact') || 'NO_CHANGE',
          impactAmount: data.get('impactAmount') ? Number(data.get('impactAmount')) : undefined,
        };
        const newAmendment = await vendorService.createAmendment(contract.id, payload);
        setAmendments((prev) => [...prev, newAmendment]);
        setShowAmendmentModal(false);
        toast.success('Amendment added successfully');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to add amendment');
      } finally {
        setModalSubmitting(false);
      }
    };

    if (amendmentsLoading) {
      return (
        <Card>
          <CardContent className="p-8 text-center text-secondary-500 dark:text-secondary-400">
            Loading amendments...
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setShowAmendmentModal(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Amendment
          </Button>
        </div>

        {amendments.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-secondary-500 dark:text-secondary-400">
              No amendments found for this contract.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Contract Amendments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Amendment #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Effective Date</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Value Impact</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Running Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                    {amendments.map((amendment) => {
                      const amColor = AMENDMENT_STATUS_COLORS[amendment.status] ?? 'secondary';
                      const impactAmount = amendment.impactAmount;
                      let impactDisplay = 'No Change';
                      let impactColorClass = 'text-secondary-500 dark:text-secondary-400';
                      if (impactAmount !== null && impactAmount !== undefined && impactAmount !== 0) {
                        if (impactAmount > 0) {
                          impactDisplay = `+${vendorService.formatCurrency(impactAmount, contract.currency)}`;
                          impactColorClass = 'text-success-600 dark:text-success-400';
                        } else {
                          impactDisplay = vendorService.formatCurrency(impactAmount, contract.currency);
                          impactColorClass = 'text-danger-600 dark:text-danger-400';
                        }
                      }
                      return (
                        <tr key={amendment.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-800/50">
                          <td className="px-4 py-3 text-sm font-mono text-secondary-900 dark:text-white">{amendment.amendmentNumber}</td>
                          <td className="px-4 py-3 text-sm text-secondary-900 dark:text-white">{amendment.title}</td>
                          <td className="px-4 py-3 text-sm text-secondary-600 dark:text-secondary-400">{vendorService.formatDate(amendment.effectiveDate)}</td>
                          <td className={cn('px-4 py-3 text-sm text-right font-medium', impactColorClass)}>{impactDisplay}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-secondary-900 dark:text-white">
                            {amendment.runningTotal !== null && amendment.runningTotal !== undefined ? vendorService.formatCurrency(amendment.runningTotal, contract.currency) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={cn('px-2.5 py-0.5 text-xs font-medium rounded-full border', getStatusBadge(amColor))}>
                              {amendment.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setEditingAmendment(amendment)}
                                className="p-1 text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                                title="Edit amendment"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeletingAmendmentId(amendment.id)}
                                className="p-1 text-secondary-400 hover:text-danger-600 dark:hover:text-danger-400 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                                title="Delete amendment"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Cumulative summary */}
              {amendments.length > 0 && (
                <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700 flex items-center justify-between px-4">
                  <span className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                    {amendments.length} amendment{amendments.length !== 1 ? 's' : ''}
                  </span>
                  <div className="text-sm">
                    <span className="text-secondary-500 dark:text-secondary-400 mr-2">Original Value:</span>
                    <span className="font-medium text-secondary-900 dark:text-white mr-4">
                      {vendorService.formatCurrency(contract.contractValue, contract.currency)}
                    </span>
                    {amendments.length > 0 && amendments[amendments.length - 1].runningTotal !== null && amendments[amendments.length - 1].runningTotal !== undefined && (
                      <>
                        <span className="text-secondary-500 dark:text-secondary-400 mr-2">Current Total:</span>
                        <span className="font-semibold text-secondary-900 dark:text-white">
                          {vendorService.formatCurrency(amendments[amendments.length - 1].runningTotal, contract.currency)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add Amendment Modal */}
        {showAmendmentModal && (
          <>
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setShowAmendmentModal(false)} />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
              <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-lg w-full pointer-events-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Add Amendment</h3>
                  <button onClick={() => setShowAmendmentModal(false)} className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleAddAmendment} className="p-6 space-y-4">
                  <div>
                    <label htmlFor="am-title" className={labelCls}>Title <span className="text-danger-500">*</span></label>
                    <input id="am-title" name="title" required placeholder="Amendment title" className={inputCls} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <DateInput id="am-effectiveDate" name="effectiveDate" label="Effective Date" />
                    </div>
                    <div>
                      <label htmlFor="am-valueImpact" className={labelCls}>Value Impact</label>
                      <select id="am-valueImpact" name="valueImpact" className={inputCls} defaultValue="NO_CHANGE">
                        <option value="NO_CHANGE">No Change</option>
                        <option value="INCREASE">Increase</option>
                        <option value="DECREASE">Decrease</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="am-impactAmount" className={labelCls}>Impact Amount</label>
                    <input id="am-impactAmount" name="impactAmount" type="number" step="0.01" placeholder="0.00" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="am-description" className={labelCls}>Description</label>
                    <textarea id="am-description" name="description" rows={3} placeholder="Describe the amendment..." className={inputCls} />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                    <Button type="button" variant="outline" onClick={() => setShowAmendmentModal(false)} disabled={modalSubmitting}>Cancel</Button>
                    <Button type="submit" disabled={modalSubmitting}>{modalSubmitting ? 'Adding...' : 'Add Amendment'}</Button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}

      {/* Edit Amendment Modal */}
      {editingAmendment && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setEditingAmendment(null)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-lg w-full pointer-events-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Edit Amendment</h3>
                <button onClick={() => setEditingAmendment(null)} className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const data = new FormData(form);
                try {
                  setModalSubmitting(true);
                  const payload: Record<string, unknown> = {
                    title: (data.get('title') as string).trim(),
                    effectiveDate: data.get('effectiveDate') || undefined,
                    valueImpact: data.get('valueImpact'),
                    impactAmount: data.get('impactAmount') ? Number(data.get('impactAmount')) : undefined,
                    description: (data.get('description') as string)?.trim() || undefined,
                    status: data.get('status'),
                  };
                  const updated = await vendorService.updateAmendment(contract.id, editingAmendment.id, payload);
                  setAmendments((prev) => prev.map((a) => a.id === updated.id ? updated : a));
                  setEditingAmendment(null);
                  toast.success('Amendment updated successfully');
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Failed to update amendment');
                } finally {
                  setModalSubmitting(false);
                }
              }} className="p-6 space-y-4">
                <div>
                  <label htmlFor="edit-amd-title" className={labelCls}>Title <span className="text-danger-500">*</span></label>
                  <input id="edit-amd-title" name="title" required defaultValue={editingAmendment.title} className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <DateInput id="edit-amd-effectiveDate" name="effectiveDate" label="Effective Date" defaultValue={editingAmendment.effectiveDate ? toDateValue(new Date(editingAmendment.effectiveDate)) : ''} />
                  </div>
                  <div>
                    <label htmlFor="edit-amd-status" className={labelCls}>Status</label>
                    <select id="edit-amd-status" name="status" defaultValue={editingAmendment.status} className={inputCls}>
                      <option value="DRAFT">Draft</option>
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                      <option value="EXECUTED">Executed</option>
                      <option value="REJECTED">Rejected</option>
                      <option value="VOIDED">Voided</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="edit-amd-valueImpact" className={labelCls}>Value Impact</label>
                    <select id="edit-amd-valueImpact" name="valueImpact" defaultValue={editingAmendment.valueImpact} className={inputCls}>
                      <option value="NO_CHANGE">No Change</option>
                      <option value="INCREASE">Increase</option>
                      <option value="DECREASE">Decrease</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="edit-amd-impactAmount" className={labelCls}>Impact Amount</label>
                    <input id="edit-amd-impactAmount" name="impactAmount" type="number" step="0.01" min="0" defaultValue={editingAmendment.impactAmount ?? ''} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="edit-amd-description" className={labelCls}>Description</label>
                  <textarea id="edit-amd-description" name="description" rows={3} defaultValue={editingAmendment.description ?? ''} className={inputCls} />
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                  <Button type="button" variant="outline" onClick={() => setEditingAmendment(null)} disabled={modalSubmitting}>Cancel</Button>
                  <Button type="submit" disabled={modalSubmitting}>{modalSubmitting ? 'Saving...' : 'Save Changes'}</Button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Amendment Confirmation */}
      {deletingAmendmentId && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setDeletingAmendmentId(null)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-sm w-full pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">Delete Amendment</h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-6">Delete this amendment? This cannot be undone.</p>
                <div className="flex justify-end space-x-3">
                  <Button variant="outline" onClick={() => setDeletingAmendmentId(null)} disabled={modalSubmitting}>Cancel</Button>
                  <Button
                    variant="destructive"
                    disabled={modalSubmitting}
                    onClick={async () => {
                      try {
                        setModalSubmitting(true);
                        await vendorService.deleteAmendment(contract.id, deletingAmendmentId);
                        setAmendments((prev) => prev.filter((a) => a.id !== deletingAmendmentId));
                        setDeletingAmendmentId(null);
                        toast.success('Amendment deleted');
                      } catch (err) {
                        toast.error(err instanceof Error ? err.message : 'Failed to delete amendment');
                      } finally {
                        setModalSubmitting(false);
                      }
                    }}
                  >
                    {modalSubmitting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    );
  }

  function renderObligationsTab() {
    if (!contract) {return null;}

    const handleAddObligation = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const form = e.currentTarget;
      const data = new FormData(form);
      try {
        setModalSubmitting(true);
        const payload: Record<string, unknown> = {
          description: (data.get('description') as string).trim(),
          obligatedParty: data.get('obligatedParty'),
          category: data.get('category'),
          dueDate: data.get('dueDate') || undefined,
          frequency: data.get('frequency') || undefined,
          status: data.get('status') || 'PENDING',
        };
        const newObligation = await vendorService.createObligation(contract.id, payload);
        setObligations((prev) => [...prev, newObligation]);
        setShowObligationModal(false);
        toast.success('Obligation added successfully');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to add obligation');
      } finally {
        setModalSubmitting(false);
      }
    };

    if (obligationsLoading) {
      return (
        <Card>
          <CardContent className="p-8 text-center text-secondary-500 dark:text-secondary-400">
            Loading obligations...
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setShowObligationModal(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Obligation
          </Button>
        </div>

        {obligations.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-secondary-500 dark:text-secondary-400">
              No obligations found for this contract.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Contract Obligations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Obligated Party</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Due Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Frequency</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                    {obligations.map((obligation) => {
                      const overdue = isOverdue(obligation.dueDate, obligation.status);
                      const obColor = overdue ? 'danger' : (OBLIGATION_STATUS_COLORS[obligation.status] ?? 'secondary');
                      const partyLabel: Record<string, string> = { US: 'Us', VENDOR: 'Vendor', BOTH: 'Both' };

                      return (
                        <tr
                          key={obligation.id}
                          className={cn(
                            'hover:bg-secondary-50 dark:hover:bg-secondary-800/50',
                            overdue && 'bg-danger-50/50 dark:bg-danger-900/10'
                          )}
                        >
                          <td className={cn(
                            'px-4 py-3 text-sm max-w-xs',
                            overdue ? 'text-danger-700 dark:text-danger-300 font-medium' : 'text-secondary-900 dark:text-white'
                          )}>
                            {obligation.description}
                          </td>
                          <td className="px-4 py-3 text-sm text-secondary-600 dark:text-secondary-400">
                            {partyLabel[obligation.obligatedParty] ?? obligation.obligatedParty}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300">
                              {obligation.category.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className={cn(
                            'px-4 py-3 text-sm',
                            overdue ? 'text-danger-600 dark:text-danger-400 font-medium' : 'text-secondary-600 dark:text-secondary-400'
                          )}>
                            {vendorService.formatDate(obligation.dueDate)}
                            {overdue && <span className="ml-1 text-xs">(overdue)</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-secondary-600 dark:text-secondary-400">
                            {obligation.frequency ? (FREQUENCY_LABELS[obligation.frequency] ?? obligation.frequency.replace(/_/g, ' ')) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={cn('px-2.5 py-0.5 text-xs font-medium rounded-full border', getStatusBadge(obColor))}>
                              {overdue ? 'OVERDUE' : obligation.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setEditingObligation(obligation)}
                                className="p-1 text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                                title="Edit obligation"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeletingObligationId(obligation.id)}
                                className="p-1 text-secondary-400 hover:text-danger-600 dark:hover:text-danger-400 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                                title="Delete obligation"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add Obligation Modal */}
        {showObligationModal && (
          <>
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setShowObligationModal(false)} />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
              <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-lg w-full pointer-events-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Add Obligation</h3>
                  <button onClick={() => setShowObligationModal(false)} className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleAddObligation} className="p-6 space-y-4">
                  <div>
                    <label htmlFor="ob-description" className={labelCls}>Description <span className="text-danger-500">*</span></label>
                    <textarea id="ob-description" name="description" required rows={3} placeholder="Describe the obligation..." className={inputCls} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="ob-obligatedParty" className={labelCls}>Obligated Party <span className="text-danger-500">*</span></label>
                      <select id="ob-obligatedParty" name="obligatedParty" required className={inputCls}>
                        <option value="US">Us</option>
                        <option value="VENDOR">Vendor</option>
                        <option value="BOTH">Both</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="ob-category" className={labelCls}>Category <span className="text-danger-500">*</span></label>
                      <select id="ob-category" name="category" required className={inputCls}>
                        <option value="PAYMENT">Payment</option>
                        <option value="DELIVERABLE">Deliverable</option>
                        <option value="MILESTONE">Milestone</option>
                        <option value="REPORTING">Reporting</option>
                        <option value="INSURANCE">Insurance</option>
                        <option value="PERFORMANCE">Performance</option>
                        <option value="DATA_HANDLING">Data Handling</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <DateInput id="ob-dueDate" name="dueDate" label="Due Date" />
                    </div>
                    <div>
                      <label htmlFor="ob-frequency" className={labelCls}>Frequency</label>
                      <select id="ob-frequency" name="frequency" className={inputCls}>
                        <option value="">One-time</option>
                        <option value="OBL_MONTHLY">Monthly</option>
                        <option value="OBL_QUARTERLY">Quarterly</option>
                        <option value="OBL_SEMI_ANNUAL">Semi-Annual</option>
                        <option value="OBL_ANNUAL">Annual</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="ob-status" className={labelCls}>Status</label>
                      <select id="ob-status" name="status" className={inputCls}>
                        <option value="PENDING">Pending</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="WAIVED">Waived</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                    <Button type="button" variant="outline" onClick={() => setShowObligationModal(false)} disabled={modalSubmitting}>Cancel</Button>
                    <Button type="submit" disabled={modalSubmitting}>{modalSubmitting ? 'Adding...' : 'Add Obligation'}</Button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}

      {/* Edit Obligation Modal */}
      {editingObligation && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setEditingObligation(null)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-lg w-full pointer-events-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Edit Obligation</h3>
                <button onClick={() => setEditingObligation(null)} className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const data = new FormData(form);
                try {
                  setModalSubmitting(true);
                  const payload: Record<string, unknown> = {
                    description: (data.get('description') as string).trim(),
                    obligatedParty: data.get('obligatedParty'),
                    category: data.get('category'),
                    dueDate: data.get('dueDate') || null,
                    frequency: data.get('frequency') || null,
                    status: data.get('status'),
                  };
                  const updated = await vendorService.updateObligation(contract.id, editingObligation.id, payload);
                  setObligations((prev) => prev.map((o) => o.id === updated.id ? updated : o));
                  setEditingObligation(null);
                  toast.success('Obligation updated successfully');
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Failed to update obligation');
                } finally {
                  setModalSubmitting(false);
                }
              }} className="p-6 space-y-4">
                <div>
                  <label htmlFor="edit-ob-description" className={labelCls}>Description <span className="text-danger-500">*</span></label>
                  <textarea id="edit-ob-description" name="description" required rows={3} defaultValue={editingObligation.description} className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="edit-ob-obligatedParty" className={labelCls}>Obligated Party <span className="text-danger-500">*</span></label>
                    <select id="edit-ob-obligatedParty" name="obligatedParty" required defaultValue={editingObligation.obligatedParty} className={inputCls}>
                      <option value="US">Us</option>
                      <option value="VENDOR">Vendor</option>
                      <option value="BOTH">Both</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="edit-ob-category" className={labelCls}>Category <span className="text-danger-500">*</span></label>
                    <select id="edit-ob-category" name="category" required defaultValue={editingObligation.category} className={inputCls}>
                      <option value="PAYMENT">Payment</option>
                      <option value="DELIVERABLE">Deliverable</option>
                      <option value="MILESTONE">Milestone</option>
                      <option value="REPORTING">Reporting</option>
                      <option value="INSURANCE">Insurance</option>
                      <option value="PERFORMANCE">Performance</option>
                      <option value="DATA_HANDLING">Data Handling</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <DateInput id="edit-ob-dueDate" name="dueDate" label="Due Date" defaultValue={editingObligation.dueDate ? toDateValue(new Date(editingObligation.dueDate)) : ''} />
                  </div>
                  <div>
                    <label htmlFor="edit-ob-frequency" className={labelCls}>Frequency</label>
                    <select id="edit-ob-frequency" name="frequency" defaultValue={editingObligation.frequency ?? ''} className={inputCls}>
                      <option value="">One-time</option>
                      <option value="OBL_MONTHLY">Monthly</option>
                      <option value="OBL_QUARTERLY">Quarterly</option>
                      <option value="OBL_SEMI_ANNUAL">Semi-Annual</option>
                      <option value="OBL_ANNUAL">Annual</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="edit-ob-status" className={labelCls}>Status <span className="text-danger-500">*</span></label>
                    <select id="edit-ob-status" name="status" required defaultValue={editingObligation.status} className={inputCls}>
                      <option value="PENDING">Pending</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="WAIVED">Waived</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                  <Button type="button" variant="outline" onClick={() => setEditingObligation(null)} disabled={modalSubmitting}>Cancel</Button>
                  <Button type="submit" disabled={modalSubmitting}>{modalSubmitting ? 'Saving...' : 'Save Changes'}</Button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Obligation Confirmation */}
      {deletingObligationId && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setDeletingObligationId(null)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-sm w-full pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">Delete Obligation</h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-6">Delete this obligation? This cannot be undone.</p>
                <div className="flex justify-end space-x-3">
                  <Button variant="outline" onClick={() => setDeletingObligationId(null)} disabled={modalSubmitting}>Cancel</Button>
                  <Button
                    variant="destructive"
                    disabled={modalSubmitting}
                    onClick={async () => {
                      try {
                        setModalSubmitting(true);
                        await vendorService.deleteObligation(contract.id, deletingObligationId);
                        setObligations((prev) => prev.filter((o) => o.id !== deletingObligationId));
                        setDeletingObligationId(null);
                        toast.success('Obligation deleted');
                      } catch (err) {
                        toast.error(err instanceof Error ? err.message : 'Failed to delete obligation');
                      } finally {
                        setModalSubmitting(false);
                      }
                    }}
                  >
                    {modalSubmitting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    );
  }

  function renderDocumentsTab() {
    if (!contract) {return null;}

    const handleUploadDocument = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!selectedFile) {
        toast.error('Please select a file');
        return;
      }
      const form = e.currentTarget;
      const data = new FormData(form);
      try {
        setModalSubmitting(true);
        setUploadProgress(0);
        const metadata: Record<string, string> = {
          displayName: (data.get('displayName') as string)?.trim() || selectedFile.name,
          category: data.get('category') as string || 'OTHER',
          confidentialityLevel: data.get('confidentialityLevel') as string || 'INTERNAL',
        };
        const desc = (data.get('description') as string)?.trim();
        if (desc) {metadata.description = desc;}
        const expDate = data.get('expirationDate') as string;
        if (expDate) {metadata.expirationDate = expDate;}

        const newDoc = await vendorService.uploadContractDocument(
          contract.id,
          selectedFile,
          metadata,
          setUploadProgress,
        );
        setDocuments((prev) => [...prev, newDoc]);
        setShowDocumentModal(false);
        setSelectedFile(null);
        toast.success('Document uploaded successfully');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to upload document');
      } finally {
        setModalSubmitting(false);
        setUploadProgress(0);
      }
    };

    if (documentsLoading) {
      return (
        <Card>
          <CardContent className="p-8 text-center text-secondary-500 dark:text-secondary-400">
            Loading documents...
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setShowDocumentModal(true)}>
            <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
            Upload Document
          </Button>
        </div>

        {documents.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-secondary-500 dark:text-secondary-400">
              No documents found for this contract.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Contract Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-800/50"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <DocumentTextIcon className="w-8 h-8 text-secondary-400 dark:text-secondary-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                          {doc.displayName}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={cn(
                            'px-2 py-0.5 text-xs font-medium rounded-full',
                            DOC_CATEGORY_COLORS[doc.category] ?? DOC_CATEGORY_COLORS.OTHER
                          )}>
                            {doc.category.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-secondary-500 dark:text-secondary-400">
                            v{doc.version}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                      <div className="text-right">
                        {doc.expirationDate && (
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">
                            Expires: {vendorService.formatDate(doc.expirationDate)}
                          </p>
                        )}
                        <p className="text-xs text-secondary-400 dark:text-secondary-500">
                          Uploaded: {vendorService.formatDate(doc.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setPreviewDocument(doc)}
                          className="p-1 text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                          title="Preview document"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingDocument(doc)}
                          className="p-1 text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                          title="Edit metadata"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletingDocumentId(doc.id)}
                          className="p-1 text-secondary-400 hover:text-danger-600 dark:hover:text-danger-400 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                          title="Delete document"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upload Document Modal */}
        {showDocumentModal && (
          <>
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => { setShowDocumentModal(false); setSelectedFile(null); }} />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
              <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-lg w-full pointer-events-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Upload Document</h3>
                  <button onClick={() => { setShowDocumentModal(false); setSelectedFile(null); }} className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleUploadDocument} className="p-6 space-y-4">
                  <div>
                    <label className={labelCls}>File <span className="text-danger-500">*</span></label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                      className="block w-full text-sm text-secondary-500 dark:text-secondary-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 dark:file:bg-primary-900/20 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-900/30"
                    />
                    {selectedFile && (
                      <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                        {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="doc-displayName" className={labelCls}>Display Name</label>
                    <input id="doc-displayName" name="displayName" placeholder={selectedFile?.name ?? 'Document name'} className={inputCls} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="doc-category" className={labelCls}>Category</label>
                      <select id="doc-category" name="category" className={inputCls} defaultValue="OTHER">
                        <option value="CONTRACT_EXECUTED">Executed Contract</option>
                        <option value="AMENDMENT">Amendment</option>
                        <option value="INSURANCE_CERTIFICATE">Insurance Certificate</option>
                        <option value="SOC_REPORT">SOC Report</option>
                        <option value="W9_TAX_FORM">W-9 Tax Form</option>
                        <option value="FINANCIAL_STATEMENT">Financial Statement</option>
                        <option value="COMPLIANCE_CERTIFICATE">Compliance Certificate</option>
                        <option value="CORRESPONDENCE">Correspondence</option>
                        <option value="PROPOSAL">Proposal</option>
                        <option value="INVOICE">Invoice</option>
                        <option value="NDA">NDA</option>
                        <option value="BACKGROUND_CHECK">Background Check</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="doc-confidentiality" className={labelCls}>Confidentiality</label>
                      <select id="doc-confidentiality" name="confidentialityLevel" className={inputCls} defaultValue="INTERNAL">
                        <option value="PUBLIC">Public</option>
                        <option value="INTERNAL">Internal</option>
                        <option value="CONFIDENTIAL">Confidential</option>
                        <option value="RESTRICTED">Restricted</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <DateInput id="doc-expirationDate" name="expirationDate" label="Expiration Date" />
                  </div>
                  <div>
                    <label htmlFor="doc-description" className={labelCls}>Description</label>
                    <textarea id="doc-description" name="description" rows={2} placeholder="Optional description..." className={inputCls} />
                  </div>
                  {modalSubmitting && uploadProgress > 0 && (
                    <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-2">
                      <div className="bg-primary-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  )}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                    <Button type="button" variant="outline" onClick={() => { setShowDocumentModal(false); setSelectedFile(null); }} disabled={modalSubmitting}>Cancel</Button>
                    <Button type="submit" disabled={modalSubmitting || !selectedFile}>{modalSubmitting ? 'Uploading...' : 'Upload'}</Button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}

      {/* Edit Document Metadata Modal */}
      {editingDocument && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setEditingDocument(null)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-lg w-full pointer-events-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Edit Document</h3>
                <button onClick={() => setEditingDocument(null)} className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const data = new FormData(form);
                try {
                  setModalSubmitting(true);
                  const payload: Record<string, string | undefined> = {
                    displayName: (data.get('displayName') as string)?.trim() || undefined,
                    category: data.get('category') as string || undefined,
                    description: (data.get('description') as string)?.trim() || undefined,
                    expirationDate: data.get('expirationDate') as string || undefined,
                    confidentialityLevel: data.get('confidentialityLevel') as string || undefined,
                  };
                  const updated = await vendorService.updateDocument(editingDocument.id, payload);
                  setDocuments((prev) => prev.map((d) => d.id === updated.id ? updated : d));
                  if (previewDocument?.id === updated.id) {setPreviewDocument(updated);}
                  setEditingDocument(null);
                  toast.success('Document updated successfully');
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Failed to update document');
                } finally {
                  setModalSubmitting(false);
                }
              }} className="p-6 space-y-4">
                <div>
                  <label htmlFor="edit-doc-displayName" className={labelCls}>Display Name</label>
                  <input id="edit-doc-displayName" name="displayName" defaultValue={editingDocument.displayName} className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="edit-doc-category" className={labelCls}>Category</label>
                    <select id="edit-doc-category" name="category" defaultValue={editingDocument.category} className={inputCls}>
                      <option value="CONTRACT_EXECUTED">Executed Contract</option>
                      <option value="AMENDMENT">Amendment</option>
                      <option value="INSURANCE_CERTIFICATE">Insurance Certificate</option>
                      <option value="SOC_REPORT">SOC Report</option>
                      <option value="W9_TAX_FORM">W-9 Tax Form</option>
                      <option value="FINANCIAL_STATEMENT">Financial Statement</option>
                      <option value="COMPLIANCE_CERTIFICATE">Compliance Certificate</option>
                      <option value="CORRESPONDENCE">Correspondence</option>
                      <option value="PROPOSAL">Proposal</option>
                      <option value="INVOICE">Invoice</option>
                      <option value="NDA">NDA</option>
                      <option value="BACKGROUND_CHECK">Background Check</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="edit-doc-confidentiality" className={labelCls}>Confidentiality</label>
                    <select id="edit-doc-confidentiality" name="confidentialityLevel" defaultValue={editingDocument.confidentialityLevel} className={inputCls}>
                      <option value="PUBLIC">Public</option>
                      <option value="INTERNAL">Internal</option>
                      <option value="CONFIDENTIAL">Confidential</option>
                      <option value="HIGHLY_CONFIDENTIAL">Highly Confidential</option>
                    </select>
                  </div>
                </div>
                <div>
                  <DateInput id="edit-doc-expirationDate" name="expirationDate" label="Expiration Date" defaultValue={editingDocument.expirationDate ? toDateValue(new Date(editingDocument.expirationDate)) : ''} />
                </div>
                <div>
                  <label htmlFor="edit-doc-description" className={labelCls}>Description</label>
                  <textarea id="edit-doc-description" name="description" rows={2} defaultValue={editingDocument.description ?? ''} className={inputCls} />
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                  <Button type="button" variant="outline" onClick={() => setEditingDocument(null)} disabled={modalSubmitting}>Cancel</Button>
                  <Button type="submit" disabled={modalSubmitting}>{modalSubmitting ? 'Saving...' : 'Save Changes'}</Button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Document Confirmation */}
      {deletingDocumentId && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setDeletingDocumentId(null)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-sm w-full pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">Delete Document</h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-6">Delete this document? This cannot be undone.</p>
                <div className="flex justify-end space-x-3">
                  <Button variant="outline" onClick={() => setDeletingDocumentId(null)} disabled={modalSubmitting}>Cancel</Button>
                  <Button
                    variant="destructive"
                    disabled={modalSubmitting}
                    onClick={async () => {
                      try {
                        setModalSubmitting(true);
                        await vendorService.deleteDocument(deletingDocumentId);
                        setDocuments((prev) => prev.filter((d) => d.id !== deletingDocumentId));
                        if (previewDocument?.id === deletingDocumentId) {setPreviewDocument(null);}
                        setDeletingDocumentId(null);
                        toast.success('Document deleted');
                      } catch (err) {
                        toast.error(err instanceof Error ? err.message : 'Failed to delete document');
                      } finally {
                        setModalSubmitting(false);
                      }
                    }}
                  >
                    {modalSubmitting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    );
  }

  function renderHistoryTab() {
    if (auditLogLoading) {
      return (
        <Card>
          <CardContent className="p-8 text-center text-secondary-500 dark:text-secondary-400">
            Loading history...
          </CardContent>
        </Card>
      );
    }

    if (auditLog.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center text-secondary-500 dark:text-secondary-400">
            No audit history found for this contract.
          </CardContent>
        </Card>
      );
    }

    const ACTION_COLORS: Record<string, string> = {
      CREATED: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
      UPDATED: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
      DELETED: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
      STATUS_CHANGED: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
      UPLOADED: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
      VERSION_UPLOADED: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
    };

    const RESOURCE_LABELS: Record<string, string> = {
      contract: 'Contract',
      contract_amendment: 'Amendment',
      contract_obligation: 'Obligation',
      vendor_document: 'Document',
    };

    function getActionColor(action: string): string {
      const key = action.split('_').slice(1).join('_');
      return ACTION_COLORS[key] ?? ACTION_COLORS.UPDATED;
    }

    function getActionLabel(action: string): string {
      return action.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
    }

    function parseChanges(oldValues?: string, newValues?: string): { field: string; from?: string; to: string }[] {
      const changes: { field: string; from?: string; to: string }[] = [];
      try {
        const newObj = newValues ? JSON.parse(newValues) : {};
        const oldObj = oldValues ? JSON.parse(oldValues) : {};
        const allKeys = new Set([...Object.keys(newObj), ...Object.keys(oldObj)]);
        for (const key of allKeys) {
          if (key === 'contractId' || key === 'id') {continue;}
          const newVal = newObj[key];
          const oldVal = oldObj[key];
          if (newVal !== undefined && oldVal !== undefined && String(newVal) !== String(oldVal)) {
            changes.push({ field: key, from: String(oldVal), to: String(newVal) });
          } else if (newVal !== undefined && oldVal === undefined) {
            changes.push({ field: key, to: String(newVal) });
          }
        }
      } catch {
        // Silently handle parse errors
      }
      return changes;
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle>Change History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {auditLog.map((entry) => {
              const changes = parseChanges(entry.oldValues, entry.newValues);
              return (
                <div key={entry.id} className="flex gap-4 p-3 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-secondary-400 dark:bg-secondary-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', getActionColor(entry.action))}>
                        {getActionLabel(entry.action)}
                      </span>
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300">
                        {RESOURCE_LABELS[entry.resourceType] ?? entry.resourceType}
                      </span>
                      <span className="text-xs text-secondary-500 dark:text-secondary-400">
                        {vendorService.formatTimestamp(entry.createdAt)}
                      </span>
                      {entry.userId && (
                        <span className="text-xs text-secondary-400 dark:text-secondary-500 font-mono">
                          {entry.userId.substring(0, 8)}...
                        </span>
                      )}
                    </div>
                    {changes.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {changes.map((change, i) => (
                          <p key={i} className="text-xs text-secondary-600 dark:text-secondary-400">
                            <span className="font-medium">{change.field.replace(/([A-Z])/g, ' $1').trim()}</span>
                            {change.from ? (
                              <span>: <span className="line-through text-secondary-400">{change.from}</span> → {change.to}</span>
                            ) : (
                              <span>: {change.to}</span>
                            )}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  // --- Main render ---

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center space-x-4 mb-6">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate('/legal/contracts')}
            className="flex-shrink-0"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-2xl font-bold text-secondary-900 dark:text-white truncate">
                {contract.title}
              </h1>
              <span className="text-sm font-mono text-secondary-500 dark:text-secondary-400">
                {contract.contractNumber}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn('px-3 py-1 text-xs font-medium rounded-full border', getStatusBadge(statusConfig.color))}>
                {statusConfig.label}
              </span>
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300">
                {vendorService.getContractTypeLabel(contract.contractType)}
              </span>
              {contract.vendor && (
                <span className="text-sm text-secondary-600 dark:text-secondary-400">
                  Vendor:{' '}
                  <Link
                    to={`/legal/vendors/${contract.vendor.id}`}
                    className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                  >
                    {contract.vendor.companyLegalName}
                  </Link>
                </span>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => { setShowEditContractModal(true); setEditContractStatus(contract.status); }}
          >
            <PencilIcon className="w-4 h-4 mr-2" />
            Edit Contract
          </Button>
        </div>
      </motion.div>

      {/* Tab Navigation */}
      <motion.div variants={itemVariants}>
        <div className="border-b border-secondary-200 dark:border-secondary-700">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300 hover:border-secondary-300 dark:hover:border-secondary-600'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </motion.div>

      {/* Tab Content */}
      <motion.div variants={itemVariants}>
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'amendments' && renderAmendmentsTab()}
        {activeTab === 'obligations' && renderObligationsTab()}
        {activeTab === 'documents' && renderDocumentsTab()}
        {activeTab === 'history' && renderHistoryTab()}
      </motion.div>

      {/* Edit Contract Modal */}
      {showEditContractModal && contract && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setShowEditContractModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-2xl w-full pointer-events-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Edit Contract</h3>
                <button onClick={() => setShowEditContractModal(false)} className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const data = new FormData(form);
                try {
                  setModalSubmitting(true);
                  const newStatus = data.get('status') as string;
                  if (newStatus !== contract.status) {
                    const comments = (data.get('statusComments') as string)?.trim() || undefined;
                    const updatedByStatus = await vendorService.updateContractStatus(contract.id, newStatus, comments);
                    setContract(updatedByStatus);
                  }
                  const payload: Record<string, unknown> = {
                    title: (data.get('title') as string).trim(),
                    description: (data.get('description') as string)?.trim() || undefined,
                    contractType: data.get('contractType'),
                    governingLaw: (data.get('governingLaw') as string)?.trim() || undefined,
                    effectiveDate: data.get('effectiveDate') || undefined,
                    expirationDate: data.get('expirationDate') || undefined,
                    noticePeriodDays: data.get('noticePeriodDays') ? Number(data.get('noticePeriodDays')) : undefined,
                    contractValue: data.get('contractValue') ? Number(data.get('contractValue')) : undefined,
                    annualValue: data.get('annualValue') ? Number(data.get('annualValue')) : undefined,
                    currency: data.get('currency'),
                    paymentTerms: data.get('paymentTerms') || undefined,
                    paymentFrequency: data.get('paymentFrequency') || undefined,
                    isAutoRenewal: data.get('isAutoRenewal') === 'true',
                    autoRenewalTermMonths: data.get('autoRenewalTermMonths') ? Number(data.get('autoRenewalTermMonths')) : undefined,
                    maxRenewalCount: data.get('maxRenewalCount') ? Number(data.get('maxRenewalCount')) : undefined,
                    confidentialityLevel: data.get('confidentialityLevel'),
                    internalNotes: (data.get('internalNotes') as string)?.trim() || undefined,
                  };
                  const updated = await vendorService.updateContract(contract.id, payload);
                  setContract(updated);
                  setShowEditContractModal(false);
                  toast.success('Contract updated successfully');
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Failed to update contract');
                } finally {
                  setModalSubmitting(false);
                }
              }} className="p-6 space-y-5">
                <div>
                  <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">Basic Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="ec-title" className={labelCls}>Title <span className="text-danger-500">*</span></label>
                      <input id="ec-title" name="title" required defaultValue={contract.title} className={inputCls} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="ec-contractType" className={labelCls}>Contract Type</label>
                        <select id="ec-contractType" name="contractType" defaultValue={contract.contractType} className={inputCls}>
                          <option value="MSA">MSA</option>
                          <option value="SOW">SOW</option>
                          <option value="NDA">NDA</option>
                          <option value="SLA">SLA</option>
                          <option value="SOFTWARE_LICENSE">Software License</option>
                          <option value="LEASE">Lease</option>
                          <option value="AMENDMENT">Amendment</option>
                          <option value="PURCHASE_ORDER">Purchase Order</option>
                          <option value="CONSULTING">Consulting</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="ec-governingLaw" className={labelCls}>Governing Law</label>
                        <input id="ec-governingLaw" name="governingLaw" defaultValue={contract.governingLaw ?? ''} placeholder="e.g. State of Delaware" className={inputCls} />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="ec-description" className={labelCls}>Description</label>
                      <textarea id="ec-description" name="description" rows={2} defaultValue={contract.description ?? ''} className={inputCls} />
                    </div>
                  </div>
                </div>
                <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4">
                  <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">Dates & Terms</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <DateInput id="ec-effectiveDate" name="effectiveDate" label="Effective Date" defaultValue={contract.effectiveDate ? toDateValue(new Date(contract.effectiveDate)) : ''} />
                    </div>
                    <div>
                      <DateInput id="ec-expirationDate" name="expirationDate" label="Expiration Date" defaultValue={contract.expirationDate ? toDateValue(new Date(contract.expirationDate)) : ''} />
                    </div>
                    <div>
                      <label htmlFor="ec-noticePeriodDays" className={labelCls}>Notice Period (days)</label>
                      <input id="ec-noticePeriodDays" name="noticePeriodDays" type="number" min="0" defaultValue={contract.noticePeriodDays ?? ''} className={inputCls} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    <div>
                      <label htmlFor="ec-isAutoRenewal" className={labelCls}>Auto-Renewal</label>
                      <select id="ec-isAutoRenewal" name="isAutoRenewal" defaultValue={contract.isAutoRenewal ? 'true' : 'false'} className={inputCls}>
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="ec-autoRenewalTermMonths" className={labelCls}>Renewal Term (months)</label>
                      <input id="ec-autoRenewalTermMonths" name="autoRenewalTermMonths" type="number" min="1" defaultValue={contract.autoRenewalTermMonths ?? ''} className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="ec-maxRenewalCount" className={labelCls}>Max Renewals</label>
                      <input id="ec-maxRenewalCount" name="maxRenewalCount" type="number" min="0" defaultValue={contract.maxRenewalCount ?? ''} className={inputCls} />
                    </div>
                  </div>
                </div>
                <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4">
                  <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">Financial</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="ec-contractValue" className={labelCls}>Contract Value</label>
                      <input id="ec-contractValue" name="contractValue" type="number" step="0.01" min="0" defaultValue={contract.contractValue ?? ''} className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="ec-annualValue" className={labelCls}>Annual Value</label>
                      <input id="ec-annualValue" name="annualValue" type="number" step="0.01" min="0" defaultValue={contract.annualValue ?? ''} className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="ec-currency" className={labelCls}>Currency</label>
                      <select id="ec-currency" name="currency" defaultValue={contract.currency} className={inputCls}>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="CAD">CAD</option>
                        <option value="AUD">AUD</option>
                        <option value="JPY">JPY</option>
                        <option value="CHF">CHF</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <label htmlFor="ec-paymentTerms" className={labelCls}>Payment Terms</label>
                      <select id="ec-paymentTerms" name="paymentTerms" defaultValue={contract.paymentTerms ?? ''} className={inputCls}>
                        <option value="">Select...</option>
                        <option value="NET_30">Net 30</option>
                        <option value="NET_45">Net 45</option>
                        <option value="NET_60">Net 60</option>
                        <option value="NET_90">Net 90</option>
                        <option value="UPON_DELIVERY">Upon Delivery</option>
                        <option value="MONTHLY">Monthly</option>
                        <option value="QUARTERLY">Quarterly</option>
                        <option value="ANNUALLY">Annually</option>
                        <option value="CUSTOM">Custom</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="ec-paymentFrequency" className={labelCls}>Payment Frequency</label>
                      <select id="ec-paymentFrequency" name="paymentFrequency" defaultValue={contract.paymentFrequency ?? ''} className={inputCls}>
                        <option value="">Select...</option>
                        <option value="ONE_TIME">One-Time</option>
                        <option value="MONTHLY">Monthly</option>
                        <option value="QUARTERLY">Quarterly</option>
                        <option value="SEMI_ANNUAL">Semi-Annual</option>
                        <option value="ANNUAL">Annual</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4">
                  <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">Classification</h4>
                  <div>
                    <label htmlFor="ec-confidentialityLevel" className={labelCls}>Confidentiality Level</label>
                    <select id="ec-confidentialityLevel" name="confidentialityLevel" defaultValue={contract.confidentialityLevel} className={inputCls}>
                      <option value="PUBLIC">Public</option>
                      <option value="INTERNAL">Internal</option>
                      <option value="CONFIDENTIAL">Confidential</option>
                      <option value="HIGHLY_CONFIDENTIAL">Highly Confidential</option>
                    </select>
                  </div>
                  <div className="mt-3">
                    <label htmlFor="ec-internalNotes" className={labelCls}>Internal Notes</label>
                    <textarea id="ec-internalNotes" name="internalNotes" rows={2} defaultValue={contract.internalNotes ?? ''} className={inputCls} />
                  </div>
                </div>
                <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4">
                  <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">Status</h4>
                  <div>
                    <label htmlFor="ec-status" className={labelCls}>Contract Status</label>
                    <select id="ec-status" name="status" value={editContractStatus} onChange={(e) => setEditContractStatus(e.target.value)} className={inputCls}>
                      <option value={contract.status}>{CONTRACT_STATUS_LABELS[contract.status] ?? contract.status} (current)</option>
                      {(CONTRACT_STATUS_TRANSITIONS[contract.status] ?? []).map((s) => (
                        <option key={s} value={s}>{CONTRACT_STATUS_LABELS[s] ?? s}</option>
                      ))}
                    </select>
                  </div>
                  {editContractStatus !== contract.status && (
                    <div className="mt-3">
                      <label htmlFor="ec-statusComments" className={labelCls}>Status Change Comments</label>
                      <textarea id="ec-statusComments" name="statusComments" rows={2} placeholder="Reason for status change..." className={inputCls} />
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                  <Button type="button" variant="outline" onClick={() => setShowEditContractModal(false)} disabled={modalSubmitting}>Cancel</Button>
                  <Button type="submit" disabled={modalSubmitting}>{modalSubmitting ? 'Saving...' : 'Save Changes'}</Button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Document Preview Drawer */}
      {previewDocument && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => { setPreviewDocument(null); setVersionFile(null); }} />
          <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white dark:bg-secondary-900 shadow-xl z-50 flex flex-col overflow-hidden">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white truncate">{previewDocument.displayName}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', DOC_CATEGORY_COLORS[previewDocument.category] ?? DOC_CATEGORY_COLORS.OTHER)}>
                    {previewDocument.category.replace(/_/g, ' ')}
                  </span>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300">
                    {previewDocument.confidentialityLevel.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
              <button onClick={() => { setPreviewDocument(null); setVersionFile(null); }} className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors ml-4">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Metadata */}
              <div>
                <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">Details</h4>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-secondary-500 dark:text-secondary-400">File Name</dt>
                  <dd className="text-secondary-900 dark:text-white truncate">{previewDocument.fileName}</dd>
                  <dt className="text-secondary-500 dark:text-secondary-400">Size</dt>
                  <dd className="text-secondary-900 dark:text-white">{formatFileSize(previewDocument.fileSize)}</dd>
                  <dt className="text-secondary-500 dark:text-secondary-400">Type</dt>
                  <dd className="text-secondary-900 dark:text-white">{previewDocument.mimeType}</dd>
                  <dt className="text-secondary-500 dark:text-secondary-400">Version</dt>
                  <dd className="text-secondary-900 dark:text-white">v{previewDocument.version}</dd>
                  <dt className="text-secondary-500 dark:text-secondary-400">Uploaded</dt>
                  <dd className="text-secondary-900 dark:text-white">{vendorService.formatDate(previewDocument.createdAt)}</dd>
                  {previewDocument.expirationDate && (
                    <>
                      <dt className="text-secondary-500 dark:text-secondary-400">Expires</dt>
                      <dd className="text-secondary-900 dark:text-white">{vendorService.formatDate(previewDocument.expirationDate)}</dd>
                    </>
                  )}
                </dl>
                {previewDocument.description && (
                  <p className="mt-3 text-sm text-secondary-600 dark:text-secondary-400">{previewDocument.description}</p>
                )}
              </div>

              {/* Download Button */}
              <Button
                className="w-full"
                onClick={async () => {
                  try {
                    await vendorService.downloadDocument(previewDocument.id, previewDocument.fileName);
                    toast.success('Download started');
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : 'Failed to download');
                  }
                }}
              >
                Download Document
              </Button>

              {/* Upload New Version */}
              <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4">
                <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">Upload New Version</h4>
                <div className="space-y-3">
                  <input
                    ref={versionFileInputRef}
                    type="file"
                    onChange={(e) => setVersionFile(e.target.files?.[0] ?? null)}
                    className="block w-full text-sm text-secondary-500 dark:text-secondary-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 dark:file:bg-primary-900/20 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-900/30"
                  />
                  {versionFile && (
                    <>
                      <textarea
                        id="version-changeNotes"
                        placeholder="Change notes (optional)..."
                        rows={2}
                        className={inputCls}
                      />
                      {versionUploading && versionProgress > 0 && (
                        <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-2">
                          <div className="bg-primary-500 h-2 rounded-full transition-all" style={{ width: `${versionProgress}%` }} />
                        </div>
                      )}
                      <Button
                        className="w-full"
                        disabled={versionUploading}
                        onClick={async () => {
                          try {
                            setVersionUploading(true);
                            setVersionProgress(0);
                            const changeNotes = (document.getElementById('version-changeNotes') as HTMLTextAreaElement)?.value?.trim() || undefined;
                            const newVersion = await vendorService.uploadDocumentVersion(previewDocument.id, versionFile, changeNotes, setVersionProgress);
                            setDocuments((prev) => prev.map((d) => d.id === previewDocument.id ? { ...d, version: newVersion.version } : d));
                            setDocumentVersions((prev) => [newVersion, ...prev]);
                            setVersionFile(null);
                            if (versionFileInputRef.current) {versionFileInputRef.current.value = '';}
                            toast.success(`Version ${newVersion.version} uploaded`);
                          } catch (err) {
                            toast.error(err instanceof Error ? err.message : 'Failed to upload version');
                          } finally {
                            setVersionUploading(false);
                            setVersionProgress(0);
                          }
                        }}
                      >
                        {versionUploading ? 'Uploading...' : 'Upload Version'}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Version History */}
              <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4">
                <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">Version History</h4>
                {versionsLoading ? (
                  <p className="text-sm text-secondary-500 dark:text-secondary-400">Loading versions...</p>
                ) : documentVersions.length === 0 ? (
                  <p className="text-sm text-secondary-500 dark:text-secondary-400">No previous versions.</p>
                ) : (
                  <div className="space-y-2">
                    {documentVersions.map((ver) => (
                      <div key={ver.id} className="flex items-center justify-between p-2 rounded-lg border border-secondary-200 dark:border-secondary-700">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-secondary-900 dark:text-white">v{ver.version}</p>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">{vendorService.formatDate(ver.createdAt)}</p>
                          {ver.changeNotes && (
                            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-0.5">{ver.changeNotes}</p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await vendorService.downloadDocument(ver.id, ver.fileName);
                            } catch (err) {
                              toast.error(err instanceof Error ? err.message : 'Failed to download');
                            }
                          }}
                        >
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}
